/**
 * The SSE fan-out contract — how a consumer receives EVERY live message
 * rather than whichever one happened to be latest at render time.
 *
 * # The defect this module exists to prevent (issue #739)
 *
 * :func:`useSse` used to expose the stream as ``lastMessage`` — a single
 * :func:`useState` slot holding only the most-recent parsed message. Every
 * consumer was an ``useEffect`` keyed on that value. When several frames
 * arrived in one ``text/event-stream`` flush (or, more generally, between
 * two React renders), the setter ran several times inside one batch, React
 * committed once, and the consumers' effects observed **only the final
 * message of the batch**. Every earlier message was silently dropped.
 *
 * That was documented-but-tolerated while the affected consumers were all
 * REST-backfilled stores: a coalesced ``detection`` / ``alert`` reappears on
 * the next poll, so the gap closed itself. The Flow-F notification surface
 * (ADR-029 §5) broke that assumption — a notification has **no backfill**, so
 * a coalesced alert is an operator interruption that never happens. Under an
 * alert flood the operator would see strictly fewer toasts than there are
 * alerts, which is exactly the condition ADR-029 §10's per-Producer flood cap
 * exists to manage.
 *
 * # The shape of the fix
 *
 * Delivery is decoupled from rendering entirely. :func:`useSse` keeps a set
 * of subscriber callbacks and invokes each one **synchronously**, from inside
 * the :class:`EventSource` listener, as each message is parsed. Nothing about
 * message delivery passes through React state, so there is no batch for
 * messages to coalesce inside — three frames in one flush produce three
 * handler invocations, in arrival order, every time.
 *
 * A queue-plus-cursor design was the alternative. It was rejected: with
 * several independent consumers, each needs its own read cursor over the
 * shared buffer, plus a retention policy for how long delivered messages
 * stay resident — which is a subscription with extra bookkeeping and a
 * memory-growth failure mode. The callback fan-out has neither.
 *
 * # Ordering and re-entrancy guarantees
 *
 * - Handlers run in subscription order, synchronously, before the emitting
 *   :class:`EventSource` listener returns.
 * - A handler that throws is isolated and logged; the remaining handlers
 *   still run (see :func:`use-sse.handleMessage`). One misbehaving bridge
 *   must never blind the operator to alerts — CLAUDE.md §8.1 fail-safe.
 * - Subscriptions are independent of the :class:`EventSource` lifecycle: a
 *   viewport change tears down and reopens the connection without
 *   disturbing who is subscribed.
 *
 * Cross-references:
 *   - map/use-sse.ts (the producer of :type:`SseSubscribe`).
 *   - map/use-map-sse-bridges.ts (the four consumers mounted off it).
 *   - ADR-029 §5 / §10 (the notification surface + flood cap);
 *     REQ-UI-006 (live update latency); ADR-016 §1 (snapshot + SSE).
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import type { SseMessage } from "@/map/sse-types";

/**
 * A consumer callback invoked once per arriving SSE message.
 *
 * Called synchronously from the :class:`EventSource` listener, so it must be
 * cheap and must not assume it is running inside a React render or effect.
 * Zustand ``getState().…`` mutations (what every bridge does) are safe here.
 */
export type SseMessageHandler = (message: SseMessage) => void;

/**
 * Register ``handler`` against the live stream. Returns the unsubscribe
 * function — call it to stop delivery (every consumer does this from an
 * effect cleanup).
 *
 * The returned function is idempotent: unsubscribing twice is harmless.
 */
export type SseSubscribe = (handler: SseMessageHandler) => () => void;

/**
 * Subscribe ``handler`` to the SSE stream for the lifetime of the calling
 * component.
 *
 * This is the hook every consumer should use rather than calling
 * :type:`SseSubscribe` directly, because it solves the stale-closure problem
 * that would otherwise force callers to memoise their handler:
 *
 * ``handler`` is held in a ref refreshed on every commit, and the
 * subscription itself depends only on ``subscribe``. So a handler closing
 * over changing values (the notification dispatch closes over the operator's
 * threshold, the session role, and the selection bus) always runs its
 * **latest** closure, while the subscription is established exactly once —
 * no re-subscribe churn, and no risk of a re-subscribe re-delivering or
 * dropping messages mid-burst.
 *
 * The ref is refreshed in a layout effect, which React runs before passive
 * effects on the same commit — so the subscription effect below can never
 * observe a stale handler on the commit that establishes it.
 *
 * @param subscribe - The connection's :type:`SseSubscribe`. ``null`` is
 *     accepted and mounts no subscription, so a consumer can render before
 *     the SSE provider is available (tests, and the pre-``onMapReady`` gap).
 * @param handler - Invoked once per arriving message. Need not be stable.
 *
 * Cross-references:
 *   - map/use-sse.ts §useSse (produces the ``subscribe`` argument).
 */
export function useSseMessages(subscribe: SseSubscribe | null, handler: SseMessageHandler): void {
  const handlerRef = useRef<SseMessageHandler>(handler);

  // Layout effect, not a render-phase assignment: mutating a ref during
  // render is unsafe under concurrent rendering (a render can be thrown
  // away). Layout effects run before the passive effect below on the same
  // commit, so the ordering the subscription relies on still holds.
  useLayoutEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (subscribe === null) {
      return undefined;
    }
    return subscribe((message) => {
      handlerRef.current(message);
    });
  }, [subscribe]);
}
